import { useState } from 'react';
import { PILLARS, calculateNextIndex, estimateRevenueImpact, getScoreColor } from '../utils/scoring';
import TickSlider from './TickSlider';

export default function WhatIfSimulator({ scores, currentIndex }) {
  // Track simulated scores for ALL pillars
  const [simScores, setSimScores] = useState(() => {
    const initial = {};
    PILLARS.forEach(p => { initial[p.id] = scores[p.id]?.score || 5; });
    return initial;
  });
  const [annualRevenue, setAnnualRevenue] = useState(1500000);

  const updateSim = (pillarId, value) => {
    setSimScores({ ...simScores, [pillarId]: value });
  };

  const resetAll = () => {
    const reset = {};
    PILLARS.forEach(p => { reset[p.id] = scores[p.id]?.score || 5; });
    setSimScores(reset);
  };

  // Calculate simulated index using all modified pillars
  const simScoresObj = {};
  PILLARS.forEach(p => {
    simScoresObj[p.id] = { score: simScores[p.id] };
  });
  const simulatedIndex = calculateNextIndex(simScoresObj);
  const totalDelta = Math.round((simulatedIndex - currentIndex) * 10) / 10;
  const revenueImpact = estimateRevenueImpact(currentIndex, simulatedIndex, annualRevenue);
  const simColor = getScoreColor(simulatedIndex);

  // Count how many pillars have been changed
  const changedCount = PILLARS.filter(p => simScores[p.id] !== (scores[p.id]?.score || 5)).length;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display text-lg text-white">What-If Simulator</h3>
        {changedCount > 0 && (
          <button onClick={resetAll} className="btn-ghost text-xs">Reset All</button>
        )}
      </div>
      <p className="text-xs text-neutral-500 mb-5">Adjust any pillar to model the impact on your overall score and revenue</p>

      {/* Annual Revenue Input */}
      <div className="mb-5">
        <label className="label-text">Estimated Annual Revenue</label>
        <div className="relative w-48">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm">$</span>
          <input
            type="number"
            value={annualRevenue}
            onChange={(e) => setAnnualRevenue(parseInt(e.target.value) || 0)}
            className="input-field text-sm pl-7"
          />
        </div>
      </div>

      {/* All Pillars */}
      <div className="space-y-3 mb-6">
        {PILLARS.map(p => {
          const current = scores[p.id]?.score || 0;
          const sim = simScores[p.id] || current;
          const changed = sim !== current;
          const pillarDelta = sim - current;
          const color = getScoreColor(sim);

          return (
            <div key={p.id} className={`rounded-lg p-3 transition-all ${changed ? 'bg-brand-gold/5 border border-brand-gold/20' : 'bg-neutral-800/30'}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-neutral-300 font-medium">{p.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-neutral-500">{current.toFixed(1)}</span>
                  {changed && (
                    <>
                      <span className="text-xs text-neutral-600">→</span>
                      <span className={`text-xs font-bold ${color.text}`}>{sim.toFixed(1)}</span>
                      <span className={`text-[10px] font-bold ${pillarDelta > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {pillarDelta > 0 ? '+' : ''}{pillarDelta.toFixed(1)}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <TickSlider value={sim} onChange={(v) => updateSim(p.id, v)} />
            </div>
          );
        })}
      </div>

      {/* Results Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-neutral-800/50 rounded-lg p-4 text-center">
          <p className="text-xs text-neutral-500 mb-1">Projected Score</p>
          <p className={`text-2xl font-black ${simColor.text}`}>
            {simulatedIndex.toFixed(1)}
          </p>
        </div>
        <div className="bg-neutral-800/50 rounded-lg p-4 text-center">
          <p className="text-xs text-neutral-500 mb-1">Total Change</p>
          <p className={`text-2xl font-black ${totalDelta > 0 ? 'text-emerald-400' : totalDelta < 0 ? 'text-red-400' : 'text-neutral-400'}`}>
            {totalDelta > 0 ? '+' : ''}{totalDelta.toFixed(1)}
          </p>
          {changedCount > 0 && <p className="text-[10px] text-neutral-600">{changedCount} pillar{changedCount !== 1 ? 's' : ''} adjusted</p>}
        </div>
        <div className="bg-neutral-800/50 rounded-lg p-4 text-center">
          <p className="text-xs text-neutral-500 mb-1">Revenue Impact</p>
          <p className={`text-2xl font-black ${revenueImpact > 0 ? 'text-emerald-400' : revenueImpact < 0 ? 'text-red-400' : 'text-neutral-400'}`}>
            {revenueImpact > 0 ? '+' : ''}${Math.abs(revenueImpact).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}
